<script lang="ts" setup generic="T">
  import { computed } from 'vue'
  import {
    CARD_BORDER_TEST_ID,
    CARD_CONTENT_TEST_ID,
    CARD_TITLE_TEST_ID,
    DEFAULT_BORDER_COLOR,
    DEFAULT_OPTION_CONTENT_KEY,
    DEFAULT_OPTION_TITLE_KEY,
    OPTION_CLICK_EVENT_NAME,
  } from './InformationCard.constants'
  import type { InformationCardVariant, Option } from './InformationCard.types'
  import GlobeIcon from '@/shared/components/icons/GlobeIcon.vue'

  interface Props {
    title?: string
    description?: string
    options: Option<T>[]
    optionTitleKey?: string
    optionContentKey?: string
    borderColor?: string
    variant?: InformationCardVariant
  }

  interface Slots {
    icon: () => void
    title: () => void
    option: () => void
    'trip-type': () => void
  }

  interface Emits {
    (e: typeof OPTION_CLICK_EVENT_NAME, option: Option<T>): void
  }

  const props = withDefaults(defineProps<Props>(), {
    optionTitleKey: DEFAULT_OPTION_TITLE_KEY,
    optionContentKey: DEFAULT_OPTION_CONTENT_KEY,
    borderColor: DEFAULT_BORDER_COLOR,
  })

  const emit = defineEmits<Emits>()

  defineSlots<Slots>()

  const onOptionClick = (option: Option<T>) => {
    if (!option.isClickable) return
    emit(OPTION_CLICK_EVENT_NAME, option)
  }

  const variantClass = computed((): string | undefined => {
    if (props.variant === 'travel') return 'card__color-border--travel'
    return 'card__color-border--custom'
  })
</script>

<template>
  <div class="card">
    <span class="card__icon">
      <slot name="icon">
        <GlobeIcon />
      </slot>
    </span>
    <div class="card__info">
      <span v-if="title" class="card__title" :data-testid="CARD_TITLE_TEST_ID">
        <slot name="title">
          {{ title }}
        </slot>
      </span>
      <span v-if="description" class="card__subtitle">{{ description }}</span>
      <ul class="card__options">
        <li
          v-for="option of options"
          :key="
            //@ts-ignore
            option[props.optionTitleKey]
          "
          class="card__option"
        >
          <slot name="option" v-bind="option">
            <span
              class="card__option-content"
              :class="{ 'card-option__content_clickable': option.isClickable }"
              :data-testid="CARD_CONTENT_TEST_ID"
              @click="onOptionClick(option)"
            >
              {{
                //@ts-ignore
                option[props.optionContentKey]
              }}
            </span>
          </slot>
        </li>
      </ul>
    </div>
    <div class="card__trip-type">
      <slot name="trip-type"></slot>
    </div>
    <div class="card__color-border" :class="variantClass" :data-testid="CARD_BORDER_TEST_ID"></div>
  </div>
</template>

<style lang="scss" scoped>
  @import './InformationCardInsuranceValues';

  .card__color-border--custom {
    background-color: v-bind('props.borderColor');
  }
</style>
